/**
 * Seva Mandir V1 rules for the pack hall phone (alignment map v1-F7.json), on top of the full
 * pack rules in ../model. PURE: every function here is unit-tested (model.test.ts).
 *
 * What V1 changes on the phone:
 *  - the line is started with one hopper photo (full clean folded into it), no scale check, no
 *    label-roll pick, no leftover from the last run;
 *  - a sack of the run's batch goes into the hopper on a scan alone (no issue, no weight);
 *  - the first pouch's QR is the only pouch check; no spot weights, rejects, last pouch, spillage;
 *  - one typed coder counter reading at the close: codes printed = this reading − the line's
 *    previous close reading (the counter is not zeroed);
 *  - dispatch picks released lots from a list and types their pouches (no carton scans).
 */
import type {
  BagView,
  PackRunView,
  ProcessState,
  RunInputView,
  RunOutputView,
} from '@/features/process/projection'
import type { Verdict } from '@/features/process/model/rules'
import type { PackLot } from '@/offline/sitePack'
import { checkPackSack } from '../model/scan'
import { checkPouchText, lotCodeFromQr, pouchesLeft } from '../model/reconcile'

const norm = (c: string) => c.trim().toUpperCase()

// ---------------------------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------------------------

const LIVE = new Set(['OPEN', 'RUNNING'])

/**
 * The pack run with the sacks and outputs this phone knows of. A started pack run is also in the
 * site list's open runs, so a hopper scan or a leftover sack made here lands on that copy; both
 * are read, each sack once.
 */
export function withSiteRun(state: ProcessState, pr: PackRunView): PackRunView {
  const site = state.runs.get(pr.id)
  if (!site) return pr
  const inputs: RunInputView[] = [...pr.inputs]
  for (const i of site.inputs)
    if (!inputs.some((x) => x.id === i.id || (x.container_id && x.container_id === i.container_id)))
      inputs.push(i)
  const outputs: RunOutputView[] = [...pr.outputs]
  for (const o of site.outputs)
    if (!outputs.some((x) => x.container_id === o.container_id)) outputs.push(o)
  return {
    ...pr,
    inputs,
    outputs,
    status:
      LIVE.has(pr.status) && !LIVE.has(site.status) && site.status !== 'CANCELLED'
        ? site.status
        : pr.status,
  }
}

/** Pack runs the office allocated to this hall that are still to be packed, oldest plan first. */
export function allocatedRuns(state: ProcessState): PackRunView[] {
  return [...state.packRuns.values()]
    .map((r) => withSiteRun(state, r))
    .filter((r) => LIVE.has(r.status) && !r.close)
    .sort((a, b) => a.planned_date.localeCompare(b.planned_date) || a.code.localeCompare(b.code))
}

/** Started on the line (the pack lot is fixed). */
export const isStarted = (r: Pick<PackRunView, 'started_at' | 'pack_lot'>) =>
  !!r.started_at && !!r.pack_lot

/** All sacks of the run's batch this phone knows of (at the hall or already tipped in). */
export function batchSacks(state: ProcessState, batchUnitId: string): BagView[] {
  return [...state.bags.values()].filter((b) => b.unit_id === batchUnitId && b.status !== 'VOID')
}

/** The batch on the home card: its sacks and recorded kilos. */
export function batchFigures(state: ProcessState, r: PackRunView): { sacks: number; g: number } {
  const sacks = batchSacks(state, r.batch_unit_id)
  if (sacks.length === 0) return { sacks: 0, g: r.planned_g }
  return {
    sacks: sacks.length,
    g: sacks.reduce((a, b) => a + (b.net_g_current ?? b.net_g_gate ?? 0), 0),
  }
}

/**
 * The batch's sacks for the run: tipped in plus still waiting at the hall (a sack tipped in leaves
 * the site list once it is consumed on the server, so both are counted).
 */
export function runSacks(state: ProcessState, r: PackRunView): { total: number; g: number } {
  const inIds = new Set(r.inputs.map((i) => i.container_id))
  const waiting = batchSacks(state, r.batch_unit_id).filter(
    (b) => b.status !== 'CONSUMED' && !inIds.has(b.container_id),
  )
  return {
    total: r.inputs.length + waiting.length,
    g: inputGrams(r) + waiting.reduce((a, b) => a + sackGrams(b), 0),
  }
}

/** Kilos tipped in so far (each sack's recorded kilos). */
export function inputGrams(r: Pick<PackRunView, 'inputs'>): number {
  return r.inputs.reduce((a, i) => a + (i.weight_g ?? 0), 0)
}

/** A sack's recorded kilos (the machine's weight; no scale at the hopper in V1). */
export const sackGrams = (b: Pick<BagView, 'net_g_current' | 'net_g_gate' | 'issue_net_g'>) =>
  b.net_g_current ?? b.issue_net_g ?? b.net_g_gate ?? 0

/**
 * F7.3 hopper scan, V1: any sack of this run's batch goes in on the scan alone (the scan is the
 * issue). A sack of another batch, on hold, used or scanned twice is refused.
 */
export function checkSackV1(state: ProcessState, run: PackRunView, code: string): Verdict<BagView> {
  const v = checkPackSack(state, run, code)
  if (v.ok || v.reason !== 'NOT_ISSUED') return v
  const bag = state.bagByPayload.get(norm(code))
  return bag ? { ok: true, value: bag } : v
}

export interface RunHold {
  held: boolean
  /** The batch's hold reason from the site list (e.g. a lab result), when one is known. */
  reason: string | null
  /** The lab is the reason. */
  lab: boolean
}

/** The batch or the pack lot is held (F7-B1.a): the line stops filling. */
export function runHold(state: ProcessState, r: PackRunView): RunHold {
  const unit = state.units.get(r.batch_unit_id)
  const lot = r.pack_lot ? (state.packLots.get(r.pack_lot.id) ?? r.pack_lot) : null
  const bagHeld = batchSacks(state, r.batch_unit_id).some(
    (b) => b.hold !== 'NONE' && b.status !== 'CONSUMED',
  )
  const held =
    r.batch_hold !== 'NONE' ||
    (!!unit && unit.hold !== 'NONE') ||
    bagHeld ||
    (!!lot && lot.hold !== 'NONE')
  const reason = unit?.hold_reason ?? lot?.hold_reason ?? null
  return { held, reason, lab: !!reason && /LAB|RESIDUE|PESTICIDE|AFLA/i.test(reason) }
}

// ---------------------------------------------------------------------------------------------
// The run's jobs (V1-F7-03a): sacks in, first pouch, fill, leftover; then close
// ---------------------------------------------------------------------------------------------

export interface RunDraft {
  /** The coder is keyed with the lot code, dates and link (F7.2). */
  keyed: boolean
  stage: RunStage
  /** The first pouch's QR matched the lot (kept here so a refreshed site list does not lose it). */
  first_ok: boolean
  /** Good pouches filled (the count travels with Close run). */
  count: number
  /** What each tap added, newest last (पिछली ठीक takes the last one back). */
  adds: number[]
  /** The hopper leftover, net grams (0 = nothing left) and its new sack's tag. */
  leftover: { g: number; tag: string | null } | null
  /** The operator saw the stop screen of a held batch and goes on to close. */
  stop_ack: boolean
  /** Close run was saved on this phone. */
  closed: { pouches: number; leftover_g: number; reading: number; lot_code: string } | null
}

export type RunStage = 'home' | 'sacks' | 'first' | 'fill' | 'leftover' | 'counter'

export const emptyRunDraft = (): RunDraft => ({
  keyed: false,
  stage: 'home',
  first_ok: false,
  count: 0,
  adds: [],
  leftover: null,
  stop_ack: false,
  closed: null,
})

export interface Jobs {
  sacks: boolean
  first: boolean
  fill: boolean
  leftover: boolean
}

export function runJobs(r: Pick<PackRunView, 'inputs' | 'first_pouch'>, d: RunDraft): Jobs {
  return {
    sacks: r.inputs.length > 0,
    first: d.first_ok || !!r.first_pouch?.match,
    fill: d.count > 0,
    leftover: d.leftover !== null,
  }
}

/** The next job to do (1–4), or 5 (close) when all four are done. */
export function nextJob(j: Jobs): 1 | 2 | 3 | 4 | 5 {
  if (!j.sacks) return 1
  if (!j.first) return 2
  if (!j.fill) return 3
  if (!j.leftover) return 4
  return 5
}

/** +1 carton / a typed count; पिछली ठीक takes the last one back. */
export function addPouches(d: RunDraft, n: number): RunDraft {
  if (n === 0) return d
  return { ...d, count: Math.max(0, d.count + n), adds: [...d.adds, n].slice(-50) }
}

export function undoPouches(d: RunDraft): RunDraft {
  const last = d.adds.at(-1)
  if (last === undefined) return d
  return { ...d, count: Math.max(0, d.count - last), adds: d.adds.slice(0, -1) }
}

/** A typed count replaces the count (kept as one step for पिछली ठीक). */
export function setPouches(d: RunDraft, n: number): RunDraft {
  return addPouches(d, Math.max(0, n) - d.count)
}

export function cartons(pouches: number, perCarton: number): { full: number; rest: number } {
  if (perCarton <= 0) return { full: 0, rest: pouches }
  return { full: Math.floor(pouches / perCarton), rest: pouches % perCarton }
}

/** Filled share of the planned pouches, 0–100. */
export const fillPct = (pouches: number, planned: number) =>
  planned > 0 ? Math.min(100, Math.round((pouches * 100) / planned)) : 0

// ---------------------------------------------------------------------------------------------
// First pouch (F7.3): the QR against the lot's link
// ---------------------------------------------------------------------------------------------

export type PouchV1 =
  | { kind: 'MATCH'; text: string }
  | { kind: 'SACK_TAG' }
  | { kind: 'MISMATCH'; text: string; lot: string | null }

/** The decoded QR text, exactly as read, against the lot's link. */
export function checkPouchV1(
  state: ProcessState,
  lot: Pick<PackLot, 'qr_url'>,
  scanned: string,
): PouchV1 {
  const text = scanned.trim()
  const known = (c: string) => state.bagByPayload.has(norm(c)) || state.deadTags.has(norm(c))
  const k = checkPouchText(text, lot.qr_url, known)
  if (k === 'MATCH') return { kind: 'MATCH', text }
  if (k === 'SACK_TAG') return { kind: 'SACK_TAG' }
  return { kind: 'MISMATCH', text, lot: lotCodeFromQr(text) }
}

/**
 * "नंबर लिखें" on the first pouch: the lot number printed under the QR (the 4 digits of the code,
 * or the date and the number). The same number as this lot reads as the lot's link; any other
 * number is a pouch of another lot. Null when nothing was typed.
 */
export function typedPouch(lot: Pick<PackLot, 'code' | 'qr_url'>, digits: string): PouchV1 | null {
  const d = digits.replace(/\D/g, '')
  if (!d) return null
  const parts = lot.code.split('-') // PL-2026-1013-0031-K
  const seq = parts[3] ?? ''
  const mmdd = parts[2] ?? ''
  const same =
    (seq !== '' && Number(d) === Number(seq) && d.length <= seq.length) || d === `${mmdd}${seq}`
  if (same) return { kind: 'MATCH', text: lot.qr_url }
  return { kind: 'MISMATCH', text: d, lot: null }
}

/**
 * The pouch's lot against the wanted lot as on the mismatch board ("…01 M" / "…01 H"): the last
 * shared characters, then where they differ.
 */
export function diffTail(got: string, want: string): { same: string; got: string; want: string } {
  let i = 0
  while (i < got.length && i < want.length && got[i] === want[i]) i++
  const same = want.slice(Math.max(0, i - 3), i)
  return { same, got: got.slice(i) || '—', want: want.slice(i) || '—' }
}

// ---------------------------------------------------------------------------------------------
// Coder (F7.2): what is keyed in, in boxes
// ---------------------------------------------------------------------------------------------

const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/** "2026-10-13" → ["13", "OCT", "2026"], as printed on the pouch. */
export function dateBoxes(isoDate: string): string[] {
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  return [String(Number(d ?? '0')).padStart(2, '0'), MON[Number(m) - 1] ?? m ?? '', y ?? '']
}

/** The QR link in boxes: the host, then each path part with its "/". */
export function qrBoxes(url: string): string[] {
  const m = /^https?:\/\/([^/]+)(\/.*)?$/.exec(url.trim())
  if (!m) return [url]
  const parts = (m[2] ?? '').split('/').filter(Boolean)
  const out = [m[1]!]
  for (const p of parts) out.push('/', p)
  return out
}

export interface CoderLines {
  lot: string[]
  packed: string[]
  bestBefore: string[]
  qr: string[]
}

export function coderLines(
  lot: Pick<PackLot, 'code' | 'pack_date' | 'best_before' | 'qr_url'>,
): CoderLines {
  return {
    lot: lot.code.split('-'),
    packed: dateBoxes(lot.pack_date),
    bestBefore: dateBoxes(lot.best_before),
    qr: qrBoxes(lot.qr_url),
  }
}

/** Best-before shown before the start (the server fixes it at the start: pack day + shelf life). */
export function bestBeforeFrom(
  packDate: string,
  shelfLifeDays: number | null | undefined,
): string | null {
  if (!shelfLifeDays) return null
  const d = new Date(`${packDate.slice(0, 10)}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + shelfLifeDays)
  return d.toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------------------------
// Close (F7.4): one counter reading
// ---------------------------------------------------------------------------------------------

export interface CodesPrinted {
  /** Codes printed for this run (sent as labels_printed). */
  printed: number
  /** printed − pouches; null when it cannot be known (no earlier reading, or the counter went back). */
  gap: number | null
  known: boolean
}

/**
 * Codes printed for the run = this reading − the line's previous close reading (the counter is
 * not zeroed). With no earlier reading on this line, or a reading below it (counter reset), the
 * printed codes are not known: the pouches are sent so no false gap is raised, and the gap is
 * left for the office (V1 open question F7.4-va).
 */
export function codesPrinted(
  reading: number,
  previous: number | null,
  pouches: number,
): CodesPrinted {
  if (previous === null || reading < previous) return { printed: pouches, gap: null, known: false }
  const printed = reading - previous
  return { printed, gap: printed - pouches, known: true }
}

/** The PACK step's loss limit when the site list has none (the processing close uses the same). */
export const PACK_LOSS_LIMIT_PCT = 0.5

export interface PackOverfill {
  /** Pouches × pack size + leftover + spillage. */
  out_g: number
  /** More out than in, beyond the allowance (0 when it fits). */
  over_g: number
  /** The step's loss limit share of the input: the server's SURPLUS line (F7.4, R28). */
  allowed_g: number
  /** Refuse the close: more came out than went in. */
  over: boolean
}

/**
 * F7.4: the close cannot put more out than went in. Pouches × pack size + leftover + spillage may
 * pass the kilos tipped in by at most the PACK loss limit (the share the server allows before it
 * raises a SURPLUS); beyond that the count is wrong. `input_g` null = a sack's kilos are not on
 * this phone: not judged here (the server still checks).
 */
export function packOverfill(
  input_g: number | null,
  pouches: number,
  netG: number,
  leftover_g: number,
  spillage_g: number,
  lossLimitPct: number | null | undefined,
): PackOverfill {
  const out_g = Math.max(0, pouches) * Math.max(0, netG) + leftover_g + spillage_g
  if (input_g === null) return { out_g, over_g: 0, allowed_g: 0, over: false }
  const allowed_g = Math.floor((input_g * (lossLimitPct ?? PACK_LOSS_LIMIT_PCT)) / 100)
  const over_g = Math.max(0, out_g - input_g - allowed_g)
  return { out_g, over_g, allowed_g, over: over_g > 0 }
}

/**
 * The most good pouches a run can hold without a SURPLUS (the largest count `packOverfill` lets
 * through): the pouch-count correction is pre-filled with it. null when the input or the pack
 * size is not on this phone.
 */
export function maxPouches(
  input_g: number | null,
  netG: number | null | undefined,
  leftover_g: number,
  spillage_g: number,
  lossLimitPct: number | null | undefined,
): number | null {
  if (input_g === null || !netG || netG <= 0) return null
  const allowed_g = Math.floor((input_g * (lossLimitPct ?? PACK_LOSS_LIMIT_PCT)) / 100)
  return Math.max(0, Math.floor((input_g + allowed_g - leftover_g - spillage_g) / netG))
}

// ---------------------------------------------------------------------------------------------
// Hall and dispatch (F7.8)
// ---------------------------------------------------------------------------------------------

export interface HallTotals {
  /** Released, not held, still at the hall. */
  released: number
  /** Closed, waiting for the office's release. */
  waiting: number
  /** On hold or recalled. */
  held: number
}

export function hallTotals(lots: readonly PackLot[]): HallTotals {
  const t: HallTotals = { released: 0, waiting: 0, held: 0 }
  for (const l of lots) {
    const left = pouchesLeft(l)
    if (left <= 0) continue
    if (l.hold !== 'NONE') t.held += left
    else if (l.status === 'RELEASED') t.released += left
    else if (l.status === 'CLOSED') t.waiting += left
  }
  return t
}

/** Lots that can go on a trip: released, not held, pouches left; oldest first. */
export function dispatchable(lots: readonly PackLot[]): PackLot[] {
  return lots
    .filter((l) => l.status === 'RELEASED' && l.hold === 'NONE' && pouchesLeft(l) > 0)
    .sort((a, b) => a.pack_date.localeCompare(b.pack_date) || a.code.localeCompare(b.code))
}

export type TripLine = { lot: PackLot; pouches: number }

/** The lots with a pouch count typed (0 / empty = not on this trip); null when one is too many. */
export function tripLines(
  lots: readonly PackLot[],
  typed: Readonly<Record<string, number>>,
): { lines: TripLine[]; over: PackLot | null } {
  const lines: TripLine[] = []
  for (const l of lots) {
    const n = typed[l.id] ?? 0
    if (n <= 0) continue
    if (n > pouchesLeft(l)) return { lines, over: l }
    lines.push({ lot: l, pouches: n })
  }
  return { lines, over: null }
}

/** The invoice number as typed: digits only, at least one. */
export const invoiceOk = (typed: string) => /^\d{1,8}$/.test(typed)
